import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Divider,
  Alert,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Stack,
  Avatar,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Snackbar,
  Slider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  PersonOff as PersonOffIcon,
  HotelOutlined as DormantIcon,
  SwapHoriz as SwapHorizIcon,
  ArrowForward as ArrowForwardIcon,
  Warning as WarningIcon,
  Timer as TimerIcon,
  Groups as GroupsIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import api from '../../services/api';

function SubTabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const ReassignmentRules = () => {
  const [subTab, setSubTab] = useState(0);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Reassignment Rules
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Automate lead reassignment for not-interested and dormant leads to maximize conversion opportunities
          </Typography>
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}>
        <Tabs
          value={subTab}
          onChange={(_, v) => setSubTab(v)}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.9rem',
              minHeight: 48,
            },
          }}
        >
          <Tab
            icon={<PersonOffIcon sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label="Not Interested Leads"
          />
          <Tab
            icon={<DormantIcon sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label="Dormant Leads"
          />
        </Tabs>
      </Box>

      <SubTabPanel value={subTab} index={0}>
        <NotInterestedLeadsTab />
      </SubTabPanel>
      <SubTabPanel value={subTab} index={1}>
        <DormantLeadsTab />
      </SubTabPanel>
    </Box>
  );
};

/* ─── Not Interested Leads Sub-tab ─── */

const NotInterestedLeadsTab = () => {
  const [rules, setRules] = useState([]);
  const [stats, setStats] = useState({ totalRules: 0, activeRules: 0, totalReassigned: 0, avgSuccessRate: 0 });
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchRules = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/lms/reassignment-rules/not-interested/');
      const data = res.data;
      if (data.success) {
        setRules(data.rules);
        setStats({
          totalRules: data.stats.total_rules,
          activeRules: data.stats.active_rules,
          totalReassigned: data.stats.total_reassigned,
          avgSuccessRate: data.stats.avg_success_rate,
        });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load rules', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const handleToggle = async (id) => {
    try {
      const res = await api.post(`/lms/reassignment-rules/not-interested/${id}/toggle/`);
      if (res.data.success) {
        setRules(prev => prev.map(r => r.id === id ? res.data.rule : r));
        setSnackbar({ open: true, message: res.data.message, severity: 'success' });
      }
    } catch {
      setSnackbar({ open: true, message: 'Failed to toggle rule', severity: 'error' });
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await api.delete(`/lms/reassignment-rules/not-interested/${id}/`);
      if (res.data.success) {
        setRules(prev => prev.filter(r => r.id !== id));
        setSnackbar({ open: true, message: res.data.message, severity: 'success' });
      }
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete rule', severity: 'error' });
    }
  };

  const handleRuleCreated = () => {
    setShowCreateDialog(false);
    fetchRules();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ bgcolor: 'primary.light', width: 40, height: 40 }}>
                  <SwapHorizIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.totalRules}</Typography>
                  <Typography variant="caption" color="text.secondary">Total Rules</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ bgcolor: 'success.light', width: 40, height: 40 }}>
                  <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.activeRules}</Typography>
                  <Typography variant="caption" color="text.secondary">Active Rules</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ bgcolor: 'info.light', width: 40, height: 40 }}>
                  <RefreshIcon sx={{ fontSize: 20, color: 'info.main' }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.totalReassigned}</Typography>
                  <Typography variant="caption" color="text.secondary">Total Reassigned</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ bgcolor: 'warning.light', width: 40, height: 40 }}>
                  <TrendingUpIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.avgSuccessRate}%</Typography>
                  <Typography variant="caption" color="text.secondary">Avg. Recovery Rate</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
        When a lead is marked <strong>"Not Interested"</strong>, these rules determine if and when the lead should be
        reassigned to another agent for a fresh attempt. A cooldown period prevents premature follow-ups.
      </Alert>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowCreateDialog(true)}>
          Create Rule
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 600 }}>Rule Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Cooldown</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Reassign To</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Source Filter</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Max Retries</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Reassigned</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Recovery %</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No rules created yet. Click "Create Rule" to get started.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              rules.map((rule) => (
                <TableRow
                  key={rule.id}
                  sx={{
                    opacity: rule.is_active ? 1 : 0.6,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{rule.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Last triggered: {rule.last_triggered_display}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      icon={<TimerIcon sx={{ fontSize: 16 }} />}
                      label={`${rule.cooldown_days} days`}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {rule.reassign_to === 'different_agent' ? 'Different Agent' : rule.reassign_to === 'same_team' ? 'Same Team' : 'Specific Agent'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(rule.agents_data || []).length} agent{(rule.agents_data || []).length !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {(rule.source_filter || []).slice(0, 2).map((s) => (
                        <Chip key={s} label={s} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                      ))}
                      {(rule.source_filter || []).length > 2 && (
                        <Chip label={`+${rule.source_filter.length - 2}`} size="small" sx={{ fontSize: '0.7rem' }} />
                      )}
                      {(!rule.source_filter || rule.source_filter.length === 0) && (
                        <Typography variant="caption" color="text.secondary">All sources</Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{rule.max_reassignments}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight={600}>{rule.total_reassigned}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                      <LinearProgress
                        variant="determinate"
                        value={Number(rule.success_rate) || 0}
                        sx={{ width: 50, height: 6, borderRadius: 3 }}
                        color={Number(rule.success_rate) >= 20 ? 'success' : Number(rule.success_rate) >= 10 ? 'warning' : 'error'}
                      />
                      <Typography variant="body2" fontWeight={500}>{Number(rule.success_rate) || 0}%</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={rule.is_active}
                      onChange={() => handleToggle(rule.id)}
                      color="success"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title="Delete rule">
                        <IconButton size="small" color="error" onClick={() => handleDelete(rule.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon fontSize="small" color="primary" /> How Not-Interested Reassignment Works
          </Typography>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {[
              { step: '1', title: 'Lead Marked Not Interested', desc: 'An agent marks a lead as "Not Interested" in the system' },
              { step: '2', title: 'Cooldown Period Starts', desc: 'The lead enters a waiting period before any reassignment attempt' },
              { step: '3', title: 'Rule Matching', desc: 'After cooldown, the system finds the first matching rule by priority' },
              { step: '4', title: 'Reassigned to New Agent', desc: 'The lead is assigned to a fresh agent from the configured pool' },
            ].map((item) => (
              <Grid item xs={12} sm={6} md={3} key={item.step}>
                <Box sx={{ textAlign: 'center', px: 1 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mx: 'auto', mb: 1, width: 32, height: 32, fontSize: 14 }}>
                    {item.step}
                  </Avatar>
                  <Typography variant="body2" fontWeight={600}>{item.title}</Typography>
                  <Typography variant="caption" color="text.secondary">{item.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <CreateNotInterestedRuleDialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)} onCreated={handleRuleCreated} />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

/* ─── Dormant Leads Sub-tab ─── */

const DormantLeadsTab = () => {
  const [rules, setRules] = useState([]);
  const [stats, setStats] = useState({ totalRules: 0, activeRules: 0, totalAffected: 0, avgRecoveryRate: 0 });
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchRules = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/lms/reassignment-rules/dormant/');
      const data = res.data;
      if (data.success) {
        setRules(data.rules);
        setStats({
          totalRules: data.stats.total_rules,
          activeRules: data.stats.active_rules,
          totalAffected: data.stats.total_affected,
          avgRecoveryRate: data.stats.avg_recovery_rate,
        });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load rules', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const handleToggle = async (id) => {
    try {
      const res = await api.post(`/lms/reassignment-rules/dormant/${id}/toggle/`);
      if (res.data.success) {
        setRules(prev => prev.map(r => r.id === id ? res.data.rule : r));
        setSnackbar({ open: true, message: res.data.message, severity: 'success' });
      }
    } catch {
      setSnackbar({ open: true, message: 'Failed to toggle rule', severity: 'error' });
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await api.delete(`/lms/reassignment-rules/dormant/${id}/`);
      if (res.data.success) {
        setRules(prev => prev.filter(r => r.id !== id));
        setSnackbar({ open: true, message: res.data.message, severity: 'success' });
      }
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete rule', severity: 'error' });
    }
  };

  const handleRuleCreated = () => {
    setShowCreateDialog(false);
    fetchRules();
  };

  const getActionLabel = (action) => {
    if (action === 'reassign') return 'Auto Reassign';
    if (action === 'notify_only') return 'Notify Only';
    return action;
  };

  const getActionColor = (action) => {
    if (action === 'reassign') return 'primary';
    if (action === 'notify_only') return 'warning';
    return 'default';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ bgcolor: 'primary.light', width: 40, height: 40 }}>
                  <DormantIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.totalRules}</Typography>
                  <Typography variant="caption" color="text.secondary">Total Rules</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ bgcolor: 'success.light', width: 40, height: 40 }}>
                  <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.activeRules}</Typography>
                  <Typography variant="caption" color="text.secondary">Active Rules</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ bgcolor: 'warning.light', width: 40, height: 40 }}>
                  <WarningIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.totalAffected}</Typography>
                  <Typography variant="caption" color="text.secondary">Leads Affected</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ bgcolor: 'info.light', width: 40, height: 40 }}>
                  <TrendingUpIcon sx={{ fontSize: 20, color: 'info.main' }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.avgRecoveryRate}%</Typography>
                  <Typography variant="caption" color="text.secondary">Avg. Recovery Rate</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
        Dormant lead rules monitor leads with <strong>no activity</strong> (calls, notes, status changes) for a
        configured period. They can auto-reassign or alert managers to prevent leads from slipping through the cracks.
        {' '}
        Only leads in <strong>New</strong>, <strong>Contacted</strong>, or <strong>Qualified</strong> stages are eligible.
      </Alert>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowCreateDialog(true)}>
          Create Rule
        </Button>
      </Box>

      {rules.length === 0 ? (
        <Paper variant="outlined" sx={{ py: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">No rules created yet. Click "Create Rule" to get started.</Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {rules.map((rule) => (
            <Card
              key={rule.id}
              variant="outlined"
              sx={{
                opacity: rule.is_active ? 1 : 0.6,
                borderLeft: 4,
                borderLeftColor: rule.is_active ? 'primary.main' : 'grey.300',
              }}
            >
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ bgcolor: rule.is_active ? 'primary.light' : 'grey.200', width: 44, height: 44 }}>
                        {rule.action === 'reassign'
                          ? <SwapHorizIcon sx={{ color: rule.is_active ? 'primary.main' : 'grey.500' }} />
                          : <WarningIcon sx={{ color: rule.is_active ? 'warning.main' : 'grey.500' }} />
                        }
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>{rule.name}</Typography>
                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                          <Chip
                            size="small"
                            label={getActionLabel(rule.action)}
                            color={getActionColor(rule.action)}
                            variant="filled"
                            sx={{ height: 22, fontSize: '0.7rem' }}
                          />
                          <Chip
                            size="small"
                            icon={<TimerIcon sx={{ fontSize: 14 }} />}
                            label={`${rule.inactivity_days} days idle`}
                            variant="outlined"
                            sx={{ height: 22, fontSize: '0.7rem' }}
                          />
                        </Stack>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={2.5}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Eligible stages
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {['New', 'Contacted', 'Qualified'].map((s) => (
                        <Chip key={s} label={s} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />
                      ))}
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      {rule.action === 'reassign' ? 'Reassign to' : 'Notify'}
                    </Typography>
                    {rule.action === 'reassign' && (rule.agents_data || []).length > 0 ? (
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {rule.reassign_strategy === 'round_robin' ? 'Round Robin' : 'Specific Team'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {rule.agents_data.length} agent{rule.agents_data.length !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    ) : (
                      <Stack direction="row" spacing={0.5}>
                        {rule.notify_manager && <Chip label="Manager" size="small" color="warning" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />}
                        {rule.notify_current_agent && <Chip label="Agent" size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />}
                      </Stack>
                    )}
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <Stack direction="row" spacing={2}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" fontWeight={700}>{rule.leads_affected}</Typography>
                        <Typography variant="caption" color="text.secondary">Affected</Typography>
                      </Box>
                      <Divider orientation="vertical" flexItem />
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" fontWeight={700} color={Number(rule.recovered_rate) >= 20 ? 'success.main' : 'warning.main'}>
                          {Number(rule.recovered_rate) || 0}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Recovered</Typography>
                      </Box>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={1.5}>
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                      <Switch
                        size="small"
                        checked={rule.is_active}
                        onChange={() => handleToggle(rule.id)}
                        color="success"
                      />
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(rule.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </Stack>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ScheduleIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                  <Typography variant="caption" color="text.disabled">
                    Last triggered {rule.last_triggered_display} &middot; Created {rule.created_at ? new Date(rule.created_at).toLocaleDateString() : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon fontSize="small" color="primary" /> How Dormant Lead Detection Works
          </Typography>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {[
              { step: '1', title: 'Activity Monitoring', desc: 'System tracks calls, notes, emails and status changes for every lead' },
              { step: '2', title: 'Inactivity Threshold', desc: 'When no activity is detected for the configured number of days' },
              { step: '3', title: 'Rule Evaluation', desc: 'Matching rules are evaluated by priority (New, Contacted, Qualified only)' },
              { step: '4', title: 'Action Triggered', desc: 'Lead is auto-reassigned or manager/agent is notified based on rule action' },
            ].map((item) => (
              <Grid item xs={12} sm={6} md={3} key={item.step}>
                <Box sx={{ textAlign: 'center', px: 1 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mx: 'auto', mb: 1, width: 32, height: 32, fontSize: 14 }}>
                    {item.step}
                  </Avatar>
                  <Typography variant="body2" fontWeight={600}>{item.title}</Typography>
                  <Typography variant="caption" color="text.secondary">{item.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <CreateDormantRuleDialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)} onCreated={handleRuleCreated} />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

/* ─── Create Not-Interested Rule Dialog ─── */

const CreateNotInterestedRuleDialog = ({ open, onClose, onCreated }) => {
  const [showLeadFilters, setShowLeadFilters] = useState(false);
  const [agents, setAgents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '',
    cooldown_days: 1,
    reassign_to: 'different_agent',
    max_reassignments: 2,
    recoverable_percentage: 60,
    source_filter: [],
    budget_min: '',
    budget_max: '',
    agent_ids: [],
  });

  const sourceOptions = [
    'manual', 'website', 'facebook', 'instagram', 'google',
    'magicbricks', '99acres', 'housing.com', 'referral', 'walk-in',
  ];

  useEffect(() => {
    if (open) {
      api.get('/lms/agent_list/').then(res => {
        // Match AssignmentRules: `agent_list` returns an array of active agents as `response.data`.
        const agentList = res.data || [];
        setAgents(Array.isArray(agentList) ? agentList : []);
      }).catch(() => setAgents([]));
    }
  }, [open]);

  const handleSubmit = async () => {
    if (form.agent_ids.length === 0) {
      setError('Please select at least one agent');
      return;
    }
    if (form.cooldown_days < 1) {
      setError('Cooldown period must be at least 1 day');
      return;
    }
    if (form.max_reassignments < 1) {
      setError('Max reassignments must be at least 1');
      return;
    }
    setError('');
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        cooldown_days: form.cooldown_days,
        reassign_to: form.reassign_to,
        max_reassignments: form.max_reassignments,
        recoverable_percentage: form.recoverable_percentage,
        source_filter: form.source_filter,
        agent_ids: form.agent_ids,
      };
      if (form.budget_min) payload.budget_min = Number(form.budget_min);
      if (form.budget_max) payload.budget_max = Number(form.budget_max);

      const res = await api.post('/lms/reassignment-rules/not-interested/', payload);
      if (res.data.success) {
        resetForm();
        onCreated();
      } else {
        setError(res.data.error || 'Failed to create rule');
      }
    } catch (err) {
      const msg = err.response?.data?.errors
        ? Object.values(err.response.data.errors).flat().join(', ')
        : 'Failed to create rule';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: '', cooldown_days: 1, reassign_to: 'different_agent',
      max_reassignments: 2, recoverable_percentage: 60,
      source_filter: [], budget_min: '', budget_max: '', agent_ids: [],
    });
    setShowLeadFilters(false);
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Not-Interested Reassignment Rule</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            fullWidth label="Rule Name" required
            value={form.name}
            onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
            placeholder="e.g., Re-engage After 30 Days"
          />

          <TextField
            fullWidth label="Cooldown Period (days)" type="number" required
            value={form.cooldown_days}
            onChange={(e) => setForm(p => ({ ...p, cooldown_days: Number(e.target.value) }))}
            inputProps={{ min: 1 }}
            helperText="Days to wait before reassigning the lead"
          />

          <FormControl fullWidth>
            <InputLabel>Reassign Strategy</InputLabel>
            <Select
              value={form.reassign_to}
              label="Reassign Strategy"
              onChange={(e) => setForm(p => ({ ...p, reassign_to: e.target.value }))}
            >
              <MenuItem value="different_agent">Different Agent (Round Robin)</MenuItem>
              <MenuItem value="same_team">Same Team (Exclude Current Agent)</MenuItem>
              <MenuItem value="specific_agent">Specific Agent</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth label="Max Reassignment Attempts" type="number"
            value={form.max_reassignments}
            onChange={(e) => setForm(p => ({ ...p, max_reassignments: Number(e.target.value) }))}
            inputProps={{ min: 1 }}
            helperText="How many times a lead can be reassigned before stopping"
          />

          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Recoverable Leads (%)
              </Typography>
              <Typography variant="body2" fontWeight="medium" color="primary">
                {form.recoverable_percentage}%
              </Typography>
            </Box>
            <Slider
              value={form.recoverable_percentage}
              min={60}
              max={100}
              step={1}
              marks={[
                { value: 60, label: '60%' },
                { value: 80, label: '80%' },
                { value: 100, label: '100%' },
              ]}
              onChange={(_, val) => setForm(p => ({ ...p, recoverable_percentage: val }))}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${v}%`}
              sx={{ color: 'primary.main' }}
            />
            <Typography variant="caption" color="text.secondary">
              Minimum percentage of not-interested leads considered recoverable (min 60%)
            </Typography>
          </Box>

          <Accordion
            expanded={showLeadFilters}
            onChange={() => setShowLeadFilters((prev) => !prev)}
            sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box>
                <Typography variant="subtitle2">Lead Filters (Optional)</Typography>
                <Typography variant="caption" color="text.secondary">
                  Leave empty to apply this rule to all not-interested leads
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Autocomplete
                  multiple
                  options={sourceOptions}
                  value={form.source_filter}
                  onChange={(_, v) => setForm(p => ({ ...p, source_filter: v }))}
                  renderInput={(params) => (
                    <TextField {...params} label="Source Filter" placeholder="All sources" size="small" />
                  )}
                />
                <Grid container spacing={2} sx={{ m: 0, width: '100%' }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth label="Min Budget" type="number" size="small"
                      value={form.budget_min}
                      onChange={(e) => setForm(p => ({ ...p, budget_min: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth label="Max Budget" type="number" size="small"
                      value={form.budget_max}
                      onChange={(e) => setForm(p => ({ ...p, budget_max: e.target.value }))}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Autocomplete
            multiple
            options={agents}
            getOptionLabel={(option) => option.agent_name || option.name || option.email || 'Unknown'}
            value={agents.filter(a => form.agent_ids.includes(a.agent_id ?? a.id))}
            onChange={(_, value) => setForm(p => ({ ...p, agent_ids: value.map(v => v.agent_id ?? v.id) }))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Assign To Agents *"
                placeholder="Select agents"
                error={form.agent_ids.length === 0}
                helperText={form.agent_ids.length === 0 ? 'At least one agent is required' : `${form.agent_ids.length} agent(s) selected`}
              />
            )}
            renderOption={(props, option) => {
              const name = option.agent_name || option.name || 'Unknown';
              const team = option.team_name || 'No Team';
              const designation = option.designation || null;
              const agentId = option.agent_id ?? option.id;

              return (
                <li {...props} key={agentId}>
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      {name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="caption" color="text.secondary">
                        {team}
                      </Typography>
                      {designation && (
                        <>
                          <Typography variant="caption" color="text.secondary">•</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {designation}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                </li>
              );
            }}
            isOptionEqualToValue={(opt, val) => (opt.agent_id ?? opt.id) === (val.agent_id ?? val.id)}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!form.name.trim() || saving || form.agent_ids.length === 0}
        >
          {saving ? <CircularProgress size={20} /> : 'Create Rule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ─── Create Dormant Rule Dialog ─── */

const CreateDormantRuleDialog = ({ open, onClose, onCreated }) => {
  const [agents, setAgents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '',
    inactivity_days: 15,
    action: 'reassign',
    reassign_strategy: 'round_robin',
    notify_manager: true,
    notify_current_agent: true,
    agent_ids: [],
  });

  useEffect(() => {
    if (open) {
      api.get('/lms/agent_list/').then(res => {
        // Match AssignmentRules: `agent_list` returns an array of active agents as `response.data`.
        const agentList = res.data || [];
        setAgents(Array.isArray(agentList) ? agentList : []);
      }).catch(() => setAgents([]));
    }
  }, [open]);

  const handleSubmit = async () => {
    if (form.action === 'reassign' && form.agent_ids.length === 0) {
      setError('Please select at least one agent');
      return;
    }
    if (form.inactivity_days < 1) {
      setError('Inactivity threshold must be at least 1 day');
      return;
    }
    setError('');
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        inactivity_days: form.inactivity_days,
        action: form.action,
        notify_manager: form.notify_manager,
        notify_current_agent: form.notify_current_agent,
        agent_ids: form.agent_ids,
      };
      if (form.action === 'reassign') {
        payload.reassign_strategy = form.reassign_strategy;
      }

      const res = await api.post('/lms/reassignment-rules/dormant/', payload);
      if (res.data.success) {
        resetForm();
        onCreated();
      } else {
        setError(res.data.error || 'Failed to create rule');
      }
    } catch (err) {
      const msg = err.response?.data?.errors
        ? Object.values(err.response.data.errors).flat().join(', ')
        : 'Failed to create rule';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: '', inactivity_days: 15, action: 'reassign', reassign_strategy: 'round_robin',
      notify_manager: true, notify_current_agent: true, agent_ids: [],
    });
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Dormant Lead Rule</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            fullWidth label="Rule Name" required
            value={form.name}
            onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
            placeholder="e.g., Inactive 15+ Days — Auto Reassign"
          />

          <TextField
            fullWidth label="Inactivity Threshold (days)" type="number" required
            value={form.inactivity_days}
            onChange={(e) => setForm(p => ({ ...p, inactivity_days: Number(e.target.value) }))}
            inputProps={{ min: 1 }}
            helperText="Number of days with no activity before rule triggers"
          />

          <FormControl fullWidth>
            <InputLabel>Action</InputLabel>
            <Select
              value={form.action}
              label="Action"
              onChange={(e) => setForm(p => ({ ...p, action: e.target.value }))}
            >
              <MenuItem value="reassign">Auto Reassign to New Agent</MenuItem>
              <MenuItem value="notify_only">Notify Only (No Reassignment)</MenuItem>
            </Select>
          </FormControl>

          {form.action === 'reassign' && (
            <FormControl fullWidth>
              <InputLabel>Reassignment Strategy</InputLabel>
              <Select
                value={form.reassign_strategy}
                label="Reassignment Strategy"
                onChange={(e) => setForm(p => ({ ...p, reassign_strategy: e.target.value }))}
              >
                <MenuItem value="round_robin">Round Robin</MenuItem>
                <MenuItem value="specific_team">Specific Team</MenuItem>
              </Select>
            </FormControl>
          )}

          <FormControlLabel
            control={<Switch checked={form.notify_manager} onChange={(e) => setForm(p => ({ ...p, notify_manager: e.target.checked }))} />}
            label="Notify manager when rule triggers"
          />
          <FormControlLabel
            control={<Switch checked={form.notify_current_agent} onChange={(e) => setForm(p => ({ ...p, notify_current_agent: e.target.checked }))} />}
            label="Notify current agent when lead is reassigned"
          />

          {form.action === 'reassign' && (
            <Autocomplete
              multiple
              options={agents}
              getOptionLabel={(option) => option.agent_name || option.name || option.email || 'Unknown'}
              value={agents.filter(a => form.agent_ids.includes(a.agent_id ?? a.id))}
              onChange={(_, value) => setForm(p => ({ ...p, agent_ids: value.map(v => v.agent_id ?? v.id) }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Reassign To Agents *"
                  placeholder="Select agents"
                  error={form.agent_ids.length === 0}
                  helperText={form.agent_ids.length === 0 ? 'At least one agent is required' : `${form.agent_ids.length} agent(s) selected`}
                />
              )}
              renderOption={(props, option) => {
                const name = option.agent_name || option.name || 'Unknown';
                const team = option.team_name || 'No Team';
                const designation = option.designation || null;
                const agentId = option.agent_id ?? option.id;

                return (
                  <li {...props} key={agentId}>
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary">
                          {team}
                        </Typography>
                        {designation && (
                          <>
                            <Typography variant="caption" color="text.secondary">•</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {designation}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Box>
                  </li>
                );
              }}
              isOptionEqualToValue={(opt, val) => (opt.agent_id ?? opt.id) === (val.agent_id ?? val.id)}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            !form.name.trim() ||
            saving ||
            (form.action === 'reassign' && form.agent_ids.length === 0)
          }
        >
          {saving ? <CircularProgress size={20} /> : 'Create Rule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReassignmentRules;
